import { t } from '../i18n/nl.js';

// Spiergroepen volgen de CHECK-constraint op exercises.muscle_group
// (migratie 20260813100000, 'armen' toegevoegd in 20260813120000) — een
// nieuwe waarde vereist dus altijd een migratie, deze constante én een
// label onder exercises.spiergroep.* in web/src/i18n/nl.js.
export const MUSCLE_GROUPS = ['benen', 'rug', 'borst', 'schouders', 'armen', 'core', 'overig'];

export function muscleGroupLabel(value) {
  return MUSCLE_GROUPS.includes(value) ? t(`exercises.spiergroep.${value}`) : value;
}

// exercises.exercise_type: 'kracht' (standaard) of 'cardio'.
export const EXERCISE_TYPES = ['kracht', 'cardio'];

export function exerciseTypeLabel(value) {
  return EXERCISE_TYPES.includes(value) ? t(`exercises.type.${value}`) : value;
}

// Cardio-metrics volgen de CHECK-constraint op exercises.cardio_metrics
// (migratie 20260814120000) — exact het MUSCLE_GROUPS-patroon hierboven.
// Een nieuwe waarde vereist dus altijd een migratie, deze constante én een
// label onder exercises.cardioMetric.* in web/src/i18n/nl.js.
export const CARDIO_METRICS = ['afstand', 'tijd', 'snelheid', 'helling', 'weerstand', 'hartslag', 'kcal'];

export function cardioMetricLabel(value) {
  return CARDIO_METRICS.includes(value) ? t(`exercises.cardioMetric.${value}`) : value;
}

// ─────────────────────────────────────────────────────────────────────────
// Onderdeel 13 — cardiodoelen in een trainingssjabloon.
//
// `template_exercise_items.cardio_targets` bewaart de doelen in exact
// dezelfde eenheden als `workout_sets` (meters, seconden, km/u, %, niveau,
// bpm, kcal), zodat "doel versus resultaat" een directe vergelijking is.
// De editor toont waar handiger een andere eenheid (km, minuten) en rekent
// dat met `factor` om — één plek, geen conversie in de pagina zelf.
//
// `tijd` heeft geen factor maar een eigen min:sec-invoer; die staat apart in
// de editor gemodelleerd (`invoer: 'minsec'`).
export const CARDIO_TARGET_CONFIG = {
  afstand: { factor: 1000, eenheid: 'km', step: '0.1', invoer: 'getal' },
  tijd: { factor: 1, eenheid: 'min:sec', step: '1', invoer: 'minsec' },
  snelheid: { factor: 1, eenheid: 'km/u', step: '0.1', invoer: 'getal' },
  helling: { factor: 1, eenheid: '%', step: '0.5', invoer: 'getal' },
  weerstand: { factor: 1, eenheid: '', step: '1', invoer: 'getal' },
  hartslag: { factor: 1, eenheid: 'bpm', step: '1', invoer: 'getal' },
  kcal: { factor: 1, eenheid: 'kcal', step: '5', invoer: 'getal' },
};

/**
 * Zet de opgeslagen doelen (basiseenheden) om naar formulierwaarden.
 * Retourneert per metric een string; `tijd` als `{ min, sec }`.
 */
export function targetsNaarFormulier(targets) {
  const resultaat = {};
  for (const metric of CARDIO_METRICS) {
    const cfg = CARDIO_TARGET_CONFIG[metric];
    const ruw = targets?.[metric];
    if (ruw === null || ruw === undefined) {
      resultaat[metric] = cfg.invoer === 'minsec' ? { min: '', sec: '' } : '';
      continue;
    }
    if (cfg.invoer === 'minsec') {
      const totaal = Math.round(Number(ruw));
      resultaat[metric] = { min: String(Math.floor(totaal / 60)), sec: String(totaal % 60) };
    } else {
      resultaat[metric] = String(Number(ruw) / cfg.factor);
    }
  }
  return resultaat;
}

/**
 * Zet formulierwaarden terug naar de opslagvorm. Lege velden verdwijnen;
 * een volledig leeg object wordt `null` (= geen doelen), zodat "geen doel"
 * precies één representatie heeft — dezelfde normalisatie als in de RPC.
 */
export function formulierNaarTargets(formulier, toegestaneMetrics = CARDIO_METRICS) {
  const resultaat = {};
  for (const metric of toegestaneMetrics) {
    const cfg = CARDIO_TARGET_CONFIG[metric];
    if (!cfg) continue;
    const waarde = formulier?.[metric];
    if (cfg.invoer === 'minsec') {
      const min = waarde?.min === '' || waarde?.min === undefined ? null : Number(waarde.min);
      const sec = waarde?.sec === '' || waarde?.sec === undefined ? null : Number(waarde.sec);
      if (min === null && sec === null) continue;
      const totaal = (min || 0) * 60 + (sec || 0);
      if (!Number.isFinite(totaal) || totaal < 0) continue;
      resultaat[metric] = totaal;
    } else {
      if (waarde === '' || waarde === null || waarde === undefined) continue;
      const getal = Number(waarde);
      if (!Number.isFinite(getal) || getal < 0) continue;
      resultaat[metric] = Math.round(getal * cfg.factor * 100) / 100;
    }
  }
  return Object.keys(resultaat).length > 0 ? resultaat : null;
}
